import React, { useCallback, useMemo } from 'react'

import { Info } from '@mui/icons-material'
import { Grid, Tooltip } from '@mui/material'
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  Edit,
  FormDataConsumer,
  ImageField,
  Labeled,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  required,
  useEditContext,
  useTranslate,
} from 'react-admin'

import { Loader, Title } from '@mantine/core'
import { useGetApiMonetizationPatterns } from '../../codegen/generated/monetization-patterns'
import { LXField } from '../../components/LXField'
import { LXNumberField } from '../../components/LXNumberField'
import { NoDeleteEditToolbar } from '../../components/NoDeleteEditToolbar'
import { L10nHelper } from '../../helpers/L10n.helper'
import { ListHelper } from '../../helpers/List.helper'
import styles from './YoutubeChannel.module.scss'

const YoutubechannelEditContent: React.FC = props => {
  const translate = useTranslate()
  const { record, isLoading } = useEditContext()

  const monetizationPatternsQuery = useGetApiMonetizationPatterns()

  const adsInsertionPresetChoices = useMemo<{ id: string; name: string }[]>(
    () =>
      (monetizationPatternsQuery.data?.body ?? []).map(p => ({
        id: p.id,
        name: p.name,
      })),
    [monetizationPatternsQuery.data?.body],
  )

  const locales = L10nHelper.getYoutubeLocales()
  const uploadTagsChoices = useMemo<{ id: string; name: string }[]>(
    () =>
      ((record?.defaultUploadTags ?? []) as string[]).map(t => ({
        id: t,
        name: t,
      })) ?? [],
    [record?.defaultUploadTags],
  )

  const onCreateDefaultTag = useCallback(
    (name?: string) => {
      if (name) {
        const tag = { id: name?.toLowerCase(), name }
        uploadTagsChoices.push(tag)

        return tag
      }
    },
    [uploadTagsChoices],
  )

  if (isLoading) {
    return <Loader size="md" />
  }

  return (
    <SimpleForm toolbar={<NoDeleteEditToolbar />}>
      <Grid container direction="row" spacing={2}>
        <Grid item lg={2}>
          <ImageField source="thumbnailUrl" label="" />
        </Grid>
        <Grid item direction="column" lg={10} spacing={2}>
          <Grid container item direction="row" lg={10} spacing={2}>
            <LXField
              source="name"
              label={translate('_generics.name')}
              elementType={TextField}
              labeled
            />
            <Grid item lg={3}>
              <Labeled label={translate('pages.youtubeChannels.subscribersCount')}>
                <LXNumberField source="subscribersCount" format="large" />
              </Labeled>
            </Grid>
            <Grid item lg={3}>
              <Labeled label={translate('pages.youtubeChannels.videosCount')}>
                <LXNumberField source="videosCount" format="large" />
              </Labeled>
            </Grid>
            <Grid item lg={3}>
              <Labeled label={translate('pages.youtubeChannels.viewsCount')}>
                <LXNumberField source="viewsCount" format="large" />
              </Labeled>
            </Grid>
            <Grid item lg={3}>
              <SelectInput
                source="cms"
                choices={[
                  { id: 'Logicomix', name: 'Logicomix' },
                  { id: 'Logicomix Network', name: 'Logicomix Network' },
                  { id: 'BUMP', name: 'Bump' },
                  { id: 'endemol', name: 'BMF' },
                  { id: 'TV5 Monde', name: 'TV5 Monde' },
                  { id: 'France TV Médias', name: 'France TV Médias' },
                ]}
                label={translate('pages.youtubeChannels.cms')}
                required
              />
            </Grid>
          </Grid>
          <hr style={{ width: '100%' }} />
          <Grid container item direction="row" lg={10} spacing={2}>
            <Grid item sm={12} lg={6}>
              <BooleanInput
                size="small"
                source="isOwnedByLogicomix"
                label={translate('pages.youtubeChannels.isOwnedByLogicomix')}
              />
            </Grid>
            <FormDataConsumer>
              {({ formData }) => (
                <Grid item sm={12} lg={6}>
                  <ReferenceInput
                    source="defaultBeneficiaryId"
                    reference="beneficiaries"
                    sort={ListHelper.DEFAULT_SORT}
                    fullWidth
                  >
                    <AutocompleteInput
                      optionText="name"
                      label={translate('pages.youtubeChannels.defaultBeneficiary')}
                    />
                  </ReferenceInput>
                  {formData.defaultBeneficiaryId && (
                    <ReferenceInput
                      source="defaultProductId"
                      reference="products"
                      filterToQuery={(searchText: string) => ({
                        q: [searchText],
                        beneficiaryId: formData.defaultBeneficiaryId,
                      })}
                      sort={ListHelper.DEFAULT_SORT}
                      fullWidth
                    >
                      <AutocompleteInput
                        optionText="name"
                        label={translate('pages.youtubeChannels.defaultProduct')}
                      />
                    </ReferenceInput>
                  )}
                </Grid>
              )}
            </FormDataConsumer>
          </Grid>

          <Title order={5} mt="xl">
            ClickUp
          </Title>
          <hr style={{ width: '100%', marginTop: '5px' }} />
          <Grid container item direction="row" lg={10} spacing={2}>
            <Grid item sm={12} lg={6}>
              <TextInput
                source="clickupListId"
                label={translate('pages.youtubeChannels.clickupListId')}
                fullWidth
              />
            </Grid>
          </Grid>

          <Title order={5} mt="xl">
            {translate('pages.youtubeChannels.tags')} &{' '}
            {translate('pages.youtubeChannels.description')}
          </Title>
          <hr style={{ width: '100%', marginTop: '5px' }} />
          <Grid container item direction="row" lg={10} spacing={2}>
            <Grid container item direction="row" lg={12} spacing={2}>
              <Grid item sm={12} lg={6}>
                <TextInput
                  source="defaultUploadDescription"
                  label={translate('pages.youtubeChannels.defaultUploadDescription')}
                  minRows={5}
                  multiline
                  fullWidth
                />
              </Grid>
              <Grid item sm={12} lg={6}>
                <AutocompleteArrayInput
                  source="defaultUploadTags"
                  choices={uploadTagsChoices}
                  optionText="name"
                  label={translate('pages.youtubeChannels.defaultUploadTags')}
                  onCreate={onCreateDefaultTag}
                />
              </Grid>
            </Grid>
            <Grid container item direction="row" lg={12} spacing={2}>
              <Grid item sm={12} lg={6}>
                <BooleanInput
                  size="small"
                  source="isAutoTagUpdateEnabled"
                  label={translate('pages.youtubeChannels.isAutoTagUpdateEnabled')}
                />
              </Grid>
              <FormDataConsumer>
                {({ formData }) =>
                  formData.isAutoTagUpdateEnabled && (
                    <>
                      <Grid item sm={12} lg={6}>
                        <TextInput
                          source="autoTags"
                          label={`${translate('pages.youtubeChannels.autoTags')} (${translate(
                            'pages.youtubeChannels.autoTagsCommaSeparated',
                          )})`}
                          style={{ width: '100%' }}
                          validate={[required()]}
                          multiline
                        />
                      </Grid>
                      <Grid item sm="auto" lg={6} />
                      <Grid item sm={12} lg={6}>
                        <AutocompleteInput
                          source="autoDefaultVideoLanguage"
                          choices={locales}
                          optionText="name"
                          label={translate('pages.youtubeChannels.autoDefaultVideoLanguage')}
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )
                }
              </FormDataConsumer>
            </Grid>
          </Grid>

          <Title order={5} mt="xl">
            {translate('pages.automatedTreatments.monetization')}
          </Title>
          <hr style={{ width: '100%', marginTop: '5px' }} />
          <Grid container item direction="row" lg={10} spacing={2}>
            <Grid container item direction="row" lg={12} spacing={2}>
              <Grid container item sm={12} lg={6}>
                <Grid item lg={10}>
                  <BooleanInput
                    size="small"
                    source="isAutoMonetizationEnabled"
                    label={translate('pages.youtubeChannels.isAutoMonetizationEnabled')}
                  />
                </Grid>
                <Grid item lg={2} style={{ position: 'relative' }}>
                  <Tooltip
                    title={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: translate('pages.youtubeChannels.autoAdsPatternTooltip'),
                        }}
                      />
                    }
                    placement="right"
                  >
                    <Info className={styles.infoEdit} />
                  </Tooltip>
                </Grid>
              </Grid>
              <FormDataConsumer>
                {({ formData }) =>
                  formData.isAutoMonetizationEnabled && (
                    <>
                      <Grid item sm={12} lg={6}>
                        <TextInput
                          source="autoMonetizationPolicyId"
                          label={translate('pages.youtubeChannels.autoMonetizationPolicyId')}
                          fullWidth
                          validate={[required()]}
                        />
                      </Grid>
                      <Grid item sm={12} lg={6}>
                        <SelectInput
                          source="adsInsertionPresetId"
                          choices={adsInsertionPresetChoices}
                          label={translate('pages.youtubeChannels.adsInsertionPresetId')}
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                    </>
                  )
                }
              </FormDataConsumer>
              <Grid item sm="auto" lg={6} />
            </Grid>
            {
              // TODO: Enable when available on API side
              // <Grid container item direction="row" lg={12} spacing={2}>
              //   <Grid item lg={6}>
              //     <BooleanInput size="small" source="isAutoInsertSubscribeEndScreenEnabled" label={translate('pages.youtubeChannels.isAutoInsertSubscribeEndScreenEnabled')} />
              //   </Grid>
              // </Grid>
            }
          </Grid>

          <Title order={5} mt="xl">
            {translate('pages.automatedTreatments.translation')}
          </Title>
          <hr style={{ width: '100%', marginTop: '5px' }} />
          <Grid container item direction="row" lg={10} spacing={2}>
            <Grid container item direction="row" lg={12} spacing={2}>
              <Grid item sm={12} lg={6}>
                <BooleanInput
                  size="small"
                  source="isAutoSubtitlesTranslationEnabled"
                  label={translate('pages.youtubeChannels.isAutoSubtitlesTranslationEnabled')}
                />
              </Grid>
              <Grid item sm={12} lg={6}>
                <FormDataConsumer>
                  {({ formData }) =>
                    formData.isAutoSubtitlesTranslationEnabled && (
                      <>
                        <AutocompleteInput
                          source="subtitleMainLanguage"
                          choices={locales}
                          optionText="name"
                          label={translate('pages.youtubeChannels.subtitleMainLanguage')}
                          validate={[required()]}
                          fullWidth
                        />
                      </>
                    )
                  }
                </FormDataConsumer>
                <FormDataConsumer>
                  {({ formData }) =>
                    formData.isAutoSubtitlesTranslationEnabled &&
                    formData.subtitleMainLanguage && (
                      <>
                        <AutocompleteArrayInput
                          source="subtitlesLanguages"
                          choices={locales.filter(({ id }) => formData.subtitleMainLanguage !== id)}
                          optionText="name"
                          label={translate('pages.youtubeChannels.additionalSubtitlesLanguages')}
                          validate={[required()]}
                          fullWidth
                        />
                      </>
                    )
                  }
                </FormDataConsumer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </SimpleForm>
  )
}

export const YoutubechannelEdit: React.FC = props => (
  <Edit {...props} mutationMode="pessimistic" redirect="show">
    <YoutubechannelEditContent {...props} />
  </Edit>
)
